import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {askTutor} from 'services/TutorChatService';
import {
  playTutorChatSpeech,
  stopTutorChatSpeech,
  getTutorSpeechPlaybackRate,
  setTutorSpeechPlaybackRate,
} from 'services/TextToSpeechService';

const TUTOR_NAME = 'Benjamin Franklin';
const HISTORICAL_FIGURE = 'Benjamin Franklin';
const PLAYBACK_RATE_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5];
const FONT_SIZE_MIN = 12;
const FONT_SIZE_MAX = 22;
const RECORDING_MAX_MS = 30_000;
const MAX_HISTORY_TURNS = 20;

let nextLocalId = 1;
function makeId() {
  nextLocalId += 1;
  return `lcv-chat-${Date.now()}-${nextLocalId}`;
}

function formatTimer(ms) {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function formatPlaybackRate(rate) {
  if (!Number.isFinite(rate)) return '1×';
  if (rate === 1) return '1×';
  return `${rate}×`;
}

/**
 * Build the `messageHistory` payload the backend expects from our richer
 * local message shape. The seeded greeting is dropped (it's a UI hint, not
 * a real model turn). Audio turns send their stored Whisper transcript so
 * the model has the actual words; the bubble shows that text like a typed
 * message, with optional audio playback when a recording URL is kept.
 */
function serializeForBackend(messages) {
  return messages
    .filter(m => !m.isGreeting)
    .map(m => {
      if (m.role === 'tutor') {
        return {sender: 'tutor', message: String(m.text || '').trim()};
      }
      const transcript = String(m.transcribedMessage || '').trim();
      const text = transcript || String(m.text || '').trim();
      if (!text) return null;
      return {sender: 'student', message: text};
    })
    .filter(Boolean)
    .slice(-MAX_HISTORY_TURNS);
}

const LCVTutorChatPanel = ({
  open,
  onClose,
  greeting,
  currentContext,
  token,
  tutorName = TUTOR_NAME,
  historicalFigure = HISTORICAL_FIGURE,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playbackRate, setPlaybackRate] = useState(() =>
    getTutorSpeechPlaybackRate(),
  );
  const [fontSize, setFontSize] = useState(15);

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const recordingStartRef = useRef(0);
  const recordingTimerRef = useRef(null);
  const recordingMaxTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesRef = useRef(messages);
  const seededGreetingRef = useRef(null);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const playbackRateIndex = PLAYBACK_RATE_OPTIONS.indexOf(playbackRate);
  const canIncreaseRate =
    playbackRateIndex >= 0 && playbackRateIndex < PLAYBACK_RATE_OPTIONS.length - 1;
  const canDecreaseRate = playbackRateIndex > 0;

  const seedGreeting = useCallback(() => {
    const text = String(greeting || 'Hi! What can I help you with?').trim();
    seededGreetingRef.current = text;
    setMessages([
      {
        id: makeId(),
        role: 'tutor',
        text,
        isGreeting: true,
      },
    ]);
  }, [greeting]);

  useEffect(() => {
    if (!open) return undefined;
    seedGreeting();
    setInput('');
    setRecordedBlob(null);
    setRecordedUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    setLoading(false);
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 320);
    return () => window.clearTimeout(focusTimer);
  }, [open, seedGreeting]);

  useEffect(() => {
    if (!open) return;
    if (
      seededGreetingRef.current != null &&
      seededGreetingRef.current !== greeting &&
      messagesRef.current.length === 1 &&
      messagesRef.current[0].isGreeting
    ) {
      seedGreeting();
    }
  }, [greeting, open, seedGreeting]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [messages, loading]);

  const cleanupMediaRecorder = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (recordingMaxTimerRef.current) {
      clearTimeout(recordingMaxTimerRef.current);
      recordingMaxTimerRef.current = null;
    }
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
  }, []);

  useEffect(
    () => () => {
      cleanupMediaRecorder();
      if (recordedUrl) URL.revokeObjectURL(recordedUrl);
      stopTutorChatSpeech();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!open) {
      cleanupMediaRecorder();
      setRecording(false);
      setRecordingMs(0);
      stopTutorChatSpeech();
    }
  }, [open, cleanupMediaRecorder]);

  const handleClose = useCallback(() => {
    stopTutorChatSpeech();
    cleanupMediaRecorder();
    onClose?.();
  }, [cleanupMediaRecorder, onClose]);

  const handleIncreaseRate = useCallback(() => {
    setPlaybackRate(current => {
      const idx = PLAYBACK_RATE_OPTIONS.indexOf(current);
      const next = Math.min(
        PLAYBACK_RATE_OPTIONS.length - 1,
        idx < 0 ? 2 : idx + 1,
      );
      return setTutorSpeechPlaybackRate(PLAYBACK_RATE_OPTIONS[next]);
    });
  }, []);

  const handleDecreaseRate = useCallback(() => {
    setPlaybackRate(current => {
      const idx = PLAYBACK_RATE_OPTIONS.indexOf(current);
      const next = Math.max(0, idx < 0 ? 2 : idx - 1);
      return setTutorSpeechPlaybackRate(PLAYBACK_RATE_OPTIONS[next]);
    });
  }, []);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recorder.state === 'inactive') return;
    try {
      recorder.stop();
    } catch {
      // ignore
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (loading || recording) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Audio recording is not supported in this browser.');
      return;
    }

    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      mediaStreamRef.current = stream;
      const recorder = new MediaRecorder(stream, {mimeType: 'audio/webm'});
      mediaRecorderRef.current = recorder;
      const chunks = [];
      recorder.ondataavailable = event => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, {type: 'audio/webm'});
        cleanupMediaRecorder();
        setRecording(false);
        setRecordingMs(0);
        if (blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setRecordedBlob(blob);
          setRecordedUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return url;
          });
        }
      };
      recordingStartRef.current = Date.now();
      setRecording(true);
      setRecordingMs(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingMs(Date.now() - recordingStartRef.current);
      }, 200);
      recordingMaxTimerRef.current = setTimeout(() => {
        stopRecording();
      }, RECORDING_MAX_MS);
      recorder.start();
    } catch (err) {
      cleanupMediaRecorder();
      setRecording(false);
      setError('Microphone access is needed to record a voice message.');
    }
  }, [cleanupMediaRecorder, loading, recording, stopRecording]);

  const clearRecordedAudio = useCallback(() => {
    setRecordedBlob(null);
    setRecordedUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const speakReply = useCallback(text => {
    if (!text) return;
    playTutorChatSpeech({text}).catch(() => {
      // Audio failure should not break the conversation flow.
    });
  }, []);

  const sendMessage = useCallback(async () => {
    if (loading) return;
    const trimmedInput = input.trim();
    const hasAudio = Boolean(recordedBlob);
    if (!trimmedInput && !hasAudio) return;
    if (!token) {
      setError('You need to be signed in to chat with the tutor.');
      return;
    }

    stopTutorChatSpeech();

    const studentTurnId = makeId();
    const optimisticTurn = hasAudio
      ? {
          id: studentTurnId,
          role: 'student',
          isAudio: true,
          audioUrl: recordedUrl,
          transcribedMessage: null,
        }
      : {
          id: studentTurnId,
          role: 'student',
          text: trimmedInput,
        };

    const historyToSend = serializeForBackend(messagesRef.current);
    setMessages(prev => [...prev, optimisticTurn]);
    setInput('');
    setError(null);
    setLoading(true);

    const payloadAudio = hasAudio ? recordedBlob : null;
    if (hasAudio) {
      setRecordedBlob(null);
      // Keep recordedUrl alive — it is now owned by the bubble.
      setRecordedUrl(null);
    }

    try {
      const response = await askTutor({
        token,
        userInput: hasAudio ? undefined : trimmedInput,
        userAudio: payloadAudio || undefined,
        tutorName,
        historicalFigure,
        messageHistory: historyToSend,
        currentContext,
      });

      const tutorText = response?.tutorResponse || '';
      const transcript = response?.transcribedMessage || '';

      setMessages(prev => {
        const next = prev.map(turn =>
          turn.id === studentTurnId && hasAudio
            ? {
                ...turn,
                transcribedMessage: transcript,
                ...(transcript ? {text: transcript} : {}),
              }
            : turn,
        );
        if (tutorText) {
          next.push({
            id: makeId(),
            role: 'tutor',
            text: tutorText,
          });
        }
        return next;
      });

      if (tutorText) {
        speakReply(tutorText);
      }
    } catch (err) {
      const message = err?.message || 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [
    currentContext,
    historicalFigure,
    input,
    loading,
    recordedBlob,
    recordedUrl,
    speakReply,
    token,
    tutorName,
  ]);

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  const sendDisabled =
    loading || (!input.trim() && !recordedBlob);

  const showTextInput = !recording && !recordedBlob;
  const showRecordingBar = recording;
  const showAudioPreview = !recording && Boolean(recordedBlob);

  const messageBubbles = useMemo(
    () =>
      messages.map(msg => {
        if (msg.role === 'tutor') {
          return (
            <div key={msg.id} className="lcv-tutor-chat-msg lcv-tutor-chat-msg-tutor">
              <div className="lcv-tutor-chat-msg-avatar" aria-hidden="true">
                🎩
              </div>
              <div className="lcv-tutor-chat-bubble lcv-tutor-chat-bubble-tutor">
                <div className="lcv-tutor-chat-bubble-text">{msg.text}</div>
              </div>
            </div>
          );
        }
        const studentText = String(
          msg.transcribedMessage || msg.text || '',
        ).trim();
        return (
          <div key={msg.id} className="lcv-tutor-chat-msg lcv-tutor-chat-msg-student">
            <div
              className={[
                'lcv-tutor-chat-bubble',
                'lcv-tutor-chat-bubble-student',
                msg.isAudio ? 'lcv-tutor-chat-bubble-voice' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {msg.isAudio ? (
                <>
                  {studentText ? (
                    <div className="lcv-tutor-chat-bubble-text">{studentText}</div>
                  ) : (
                    <div className="lcv-tutor-chat-bubble-text lcv-tutor-chat-voice-pending">
                      Voice message
                    </div>
                  )}
                  {msg.audioUrl ? (
                    <audio
                      src={msg.audioUrl}
                      controls
                      className="lcv-tutor-chat-audio"
                    />
                  ) : null}
                </>
              ) : (
                <div className="lcv-tutor-chat-bubble-text">{msg.text}</div>
              )}
            </div>
          </div>
        );
      }),
    [messages],
  );

  return (
    <div
      className={[
        'lcv-tutor-chat-panel',
        open ? 'lcv-tutor-chat-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden={!open}
    >
      <div className="lcv-tutor-chat-header">
        <div className="lcv-tutor-chat-avatar" aria-hidden="true">
          🎩
        </div>
        <div className="lcv-tutor-chat-header-info">
          <div className="lcv-tutor-chat-name">{tutorName}</div>
          <div className="lcv-tutor-chat-status">
            <span className="lcv-tutor-chat-status-dot" />
            Available to help
          </div>
        </div>
        <button
          type="button"
          className="lcv-tutor-chat-close"
          onClick={handleClose}
          title="Minimize"
          aria-label="Close tutor chat"
        >
          ▾
        </button>
      </div>

      <div className="lcv-tutor-chat-toolbar">
        <div className="lcv-tutor-tool-group">
          <span className="lcv-tutor-tool-label">Speed</span>
          <button
            type="button"
            className="lcv-tutor-tool-btn"
            onClick={handleDecreaseRate}
            disabled={!canDecreaseRate}
            aria-label="Decrease tutor speed"
          >
            -
          </button>
          <span className="lcv-tutor-tool-value">{formatPlaybackRate(playbackRate)}</span>
          <button
            type="button"
            className="lcv-tutor-tool-btn"
            onClick={handleIncreaseRate}
            disabled={!canIncreaseRate}
            aria-label="Increase tutor speed"
          >
            +
          </button>
        </div>
        <div className="lcv-tutor-tool-group">
          <span className="lcv-tutor-tool-label">Text</span>
          <button
            type="button"
            className="lcv-tutor-tool-btn"
            disabled={fontSize <= FONT_SIZE_MIN}
            onClick={() => setFontSize(s => Math.max(FONT_SIZE_MIN, s - 1))}
            aria-label="Decrease text size"
          >
            A-
          </button>
          <span className="lcv-tutor-tool-value">{fontSize}px</span>
          <button
            type="button"
            className="lcv-tutor-tool-btn"
            disabled={fontSize >= FONT_SIZE_MAX}
            onClick={() => setFontSize(s => Math.min(FONT_SIZE_MAX, s + 1))}
            aria-label="Increase text size"
          >
            A+
          </button>
        </div>
      </div>

      <div
        className="lcv-tutor-chat-messages"
        style={{fontSize: `${fontSize}px`}}
      >
        {messageBubbles}
        {loading && (
          <div className="lcv-tutor-chat-msg lcv-tutor-chat-msg-tutor">
            <div className="lcv-tutor-chat-msg-avatar" aria-hidden="true">
              🎩
            </div>
            <div className="lcv-tutor-chat-bubble lcv-tutor-chat-bubble-tutor lcv-tutor-chat-bubble-typing">
              <span className="lcv-tutor-chat-typing-dot" />
              <span className="lcv-tutor-chat-typing-dot" />
              <span className="lcv-tutor-chat-typing-dot" />
            </div>
          </div>
        )}
        {error && (
          <div className="lcv-tutor-chat-error" role="alert">
            {error}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="lcv-tutor-chat-input-area">
        <div className="lcv-tutor-chat-input-row">
          {!recording ? (
            <button
              type="button"
              className="lcv-tutor-chat-mic-btn"
              onClick={startRecording}
              disabled={loading}
              title={recordedBlob ? 'Re-record voice message' : 'Record voice message'}
              aria-label={recordedBlob ? 'Re-record voice message' : 'Record voice message'}
            >
              🎙️
            </button>
          ) : (
            <button
              type="button"
              className="lcv-tutor-chat-mic-btn lcv-tutor-chat-mic-recording"
              onClick={stopRecording}
              title="Stop recording"
              aria-label="Stop recording"
            >
              ■
            </button>
          )}

          {showTextInput && (
            <textarea
              ref={inputRef}
              className="lcv-tutor-chat-input"
              placeholder="Type your question…"
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
          )}

          {showRecordingBar && (
            <div className="lcv-tutor-chat-recording-bar" role="status">
              <span className="lcv-tutor-chat-recording-pulse" aria-hidden="true" />
              <span className="lcv-tutor-chat-recording-label">Recording…</span>
              <span className="lcv-tutor-chat-recording-timer">
                {formatTimer(recordingMs)}
              </span>
            </div>
          )}

          {showAudioPreview && (
            <div className="lcv-tutor-chat-audio-preview">
              <audio
                src={recordedUrl || undefined}
                controls
                className="lcv-tutor-chat-audio"
              />
              <button
                type="button"
                className="lcv-tutor-chat-audio-remove"
                onClick={clearRecordedAudio}
                title="Discard recording"
                aria-label="Discard recording"
              >
                ✕
              </button>
            </div>
          )}

          <button
            type="button"
            className="lcv-tutor-chat-send-btn"
            onClick={sendMessage}
            disabled={sendDisabled}
            aria-label="Send message"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
};

export default LCVTutorChatPanel;
